import { PAGE_PATHS, parseCommand, type Intent } from '@ly360/core';
import { zonedWallTimeToIso } from '@ly360/i18n';
import { setLocale, setThemePref, useFormat } from '@ly360/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  Bot,
  Briefcase,
  Building2,
  CheckSquare,
  CircleDot,
  Command,
  Languages,
  Megaphone,
  MessageSquare,
  Mic,
  Moon,
  Newspaper,
  Search,
  Square,
  UserRound,
  Workflow,
} from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

interface Row {
  key: string;
  icon: ReactNode;
  label: string;
  hint?: string;
  run: () => Promise<string | void> | string | void;
}

/** The internal assistant: Ctrl/⌘ K, then type to go somewhere, find someone, or do something. */
export function CommandBar() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <>
      <button
        type="button"
        className="cmd-trigger"
        onClick={() => setOpen(true)}
        aria-label={t('assistant.open')}
        data-testid="assistant-open"
      >
        <Search size={15} aria-hidden="true" />
        <span className="hide-sm">{t('assistant.trigger')}</span>
        <kbd className="hide-sm" aria-hidden="true">
          <Command size={11} />K
        </kbd>
      </button>
      {/* Portalled: the header's backdrop blur would otherwise contain the fixed overlay. */}
      {open && createPortal(<Palette onClose={() => setOpen(false)} />, document.body)}
    </>
  );
}

function Palette({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const fmt = useFormat();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const { session, savePreferences } = useAuth();
  const [text, setText] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState(0);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const ar = i18n.language === 'ar' || i18n.language === 'ur';

  const conversationId =
    location.pathname === '/inbox' ? new URLSearchParams(location.search).get('c') : null;

  useEffect(() => input.current?.focus(), []);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(text.trim()), 200);
    return () => clearTimeout(h);
  }, [text]);

  const intents = useMemo(
    () => parseCommand(text, { inConversation: !!conversationId }),
    [text, conversationId],
  );
  const searchQ = intents.find((i) => i.kind === 'search') ? debounced : '';
  const results = useQuery({
    queryKey: ['assistant-search', searchQ],
    enabled: searchQ.length >= 2,
    staleTime: 10_000,
    queryFn: async () => {
      const q = encodeURIComponent(searchQ);
      const [people, companies, cases] = await Promise.all([
        api<{ items: { id: string; displayName: string; jobTitle: string | null }[] }>(
          `/v1/people?q=${q}&limit=5`,
        ).catch(() => ({ items: [] })),
        api<{ items: { id: string; name: string; nameAr: string | null }[] }>(
          `/v1/accounts?q=${q}&limit=5`,
        ).catch(() => ({
          items: [],
        })),
        api<{ items: { id: string; number: string; subject: string }[] }>(
          `/v1/cases?q=${q}&status=all`,
        ).catch(() => ({
          items: [],
        })),
      ]);
      return {
        people: people.items.slice(0, 5),
        companies: companies.items.slice(0, 5),
        cases: cases.items.slice(0, 5),
      };
    },
  });

  const dueIso = (days: number) => {
    const day = new Intl.DateTimeFormat('en-CA', { timeZone: fmt.timezone }).format(
      new Date(Date.now() + days * 86_400_000),
    );
    return zonedWallTimeToIso(`${day}T09:00`, fmt.timezone);
  };

  const describe = (i: Intent): Row | null => {
    switch (i.kind) {
      case 'navigate':
        return {
          key: `nav-${i.page}`,
          icon: <ArrowRight size={15} />,
          label: t('assistant.goTo', { page: t(`assistant.pages.${i.page}`) }),
          run: () => navigate(PAGE_PATHS[i.page]),
        };
      case 'createTask': {
        const due = i.due ? dueIso(i.due.days) : null;
        return {
          key: 'task',
          icon: <CheckSquare size={15} />,
          label: t('assistant.createTask', { title: i.title }),
          hint: due ? t('assistant.dueAt', { when: fmt.dateTime(due) }) : t('assistant.noDue'),
          run: async () => {
            await api('/v1/tasks', {
              method: 'POST',
              body: JSON.stringify({
                title: i.title,
                dueAt: due,
                assigneeUserId: session?.user.id,
                ...(conversationId ? { relatedType: 'conversation', relatedId: conversationId } : {}),
              }),
            });
            void qc.invalidateQueries({ queryKey: ['tasks'] });
            return t('assistant.taskCreated', { title: i.title });
          },
        };
      }
      case 'createCase':
        return {
          key: 'case',
          icon: <Briefcase size={15} />,
          label: t('assistant.createCase', { subject: i.subject || t('assistant.untitled') }),
          hint: t('assistant.forThisCustomer'),
          run: async () => {
            const c = await api<{ conversation: unknown; person: { id: string } }>(
              `/v1/conversations/${conversationId}`,
            );
            const r = await api<{ id: string }>('/v1/cases', {
              method: 'POST',
              body: JSON.stringify({
                subject: i.subject || t('assistant.untitled'),
                personId: c.person.id,
                conversationId,
              }),
            });
            navigate(`/cases/${r.id}`);
          },
        };
      case 'conversation':
        return {
          key: `conv-${i.action}`,
          icon: <MessageSquare size={15} />,
          label: t(`assistant.conversation.${i.action}`),
          run: async () => {
            const patch =
              i.action === 'assignToMe'
                ? { assigneeUserId: session?.user.id }
                : { status: i.action === 'close' ? 'closed' : 'open' };
            await api(`/v1/conversations/${conversationId}`, {
              method: 'PATCH',
              body: JSON.stringify(patch),
            });
            void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
            void qc.invalidateQueries({ queryKey: ['conversations'] });
            return t('assistant.doneConversation');
          },
        };
      case 'status':
        return {
          key: 'status',
          icon: <CircleDot size={15} />,
          label: t('assistant.setStatus', { status: t(`calendar.status.${i.status}`) }),
          run: async () => {
            await api('/v1/me/availability', { method: 'PUT', body: JSON.stringify({ status: i.status }) });
            void qc.invalidateQueries({ queryKey: ['calendar'] });
            return t('calendar.statusIs', { status: t(`calendar.status.${i.status}`) });
          },
        };
      case 'theme':
        return {
          key: 'theme',
          icon: <Moon size={15} />,
          label: t('assistant.setTheme', { theme: t(`common.theme.${i.theme}`) }),
          run: () => {
            setThemePref(i.theme);
            return t('assistant.themeSet');
          },
        };
      case 'language':
        return {
          key: 'lang',
          icon: <Languages size={15} />,
          label: t('assistant.setLanguage', { language: i.locale === 'ar' ? 'العربية' : 'English' }),
          run: async () => {
            setLocale(i.locale);
            await savePreferences({ locale: i.locale });
          },
        };
      case 'automation': {
        const name = t(`automations.recipe.${i.recipe}.name`);
        const what =
          i.set.enabled === false
            ? t('assistant.cmd.automationOff', { name })
            : i.set.mode
              ? t(`assistant.cmd.automationMode.${i.set.mode}`, { name })
              : t('assistant.cmd.automationOn', { name });
        return {
          key: `auto-${i.recipe}`,
          icon: <Workflow size={15} />,
          label: what,
          run: async () => {
            const list = await api<{ items: { id: string; recipe: string | null }[] }>('/v1/automations');
            const a = list.items.find((x) => x.recipe === i.recipe);
            if (!a) throw new Error(name);
            await api(`/v1/automations/${a.id}`, { method: 'PATCH', body: JSON.stringify(i.set) });
            void qc.invalidateQueries({ queryKey: ['automations'] });
            return t('assistant.cmd.done', { what });
          },
        };
      }
      case 'assistantSwitch': {
        const what = i.on ? t('assistant.cmd.assistantOn') : t('assistant.cmd.assistantOff');
        return {
          key: 'assistant-switch',
          icon: <Bot size={15} />,
          label: what,
          run: async () => {
            await api('/v1/assistant/settings', { method: 'PUT', body: JSON.stringify({ enabled: i.on }) });
            void qc.invalidateQueries({ queryKey: ['assistant'] });
            return t('assistant.cmd.done', { what });
          },
        };
      }
      case 'followUp':
        return {
          key: 'follow-up',
          icon: <Megaphone size={15} />,
          label: t('assistant.cmd.followUp', { about: i.about, count: i.days }),
          hint: t('assistant.cmd.followUpHint'),
          run: () => {
            navigate('/campaigns', {
              state: {
                preset: {
                  name: t('assistant.cmd.followUpName', { about: i.about }),
                  segmentName: t('assistant.cmd.followUpSegment', { about: i.about }),
                  rules: [{ type: 'asked', words: [i.about], days: i.days, notBought: true }],
                },
              },
            });
          },
        };
      case 'post':
        return {
          key: 'post',
          icon: <Newspaper size={15} />,
          label: t('assistant.cmd.post', { text: i.text }),
          hint: i.due
            ? t('assistant.dueAt', { when: fmt.dateTime(dueIso(i.due.days)!) })
            : t('assistant.cmd.postNow'),
          run: () => {
            navigate('/publishing', {
              state: { compose: { text: i.text, scheduledAt: i.due ? dueIso(i.due.days) : null } },
            });
          },
        };
      case 'search':
        return null;
    }
  };

  const rows: { group: string; rows: Row[] }[] = [];
  const actions = intents.map(describe).filter((r): r is Row => !!r);
  if (actions.length) rows.push({ group: t('assistant.actions'), rows: actions });
  const r = results.data;
  if (r?.people.length)
    rows.push({
      group: t('nav.people'),
      rows: r.people.map((p) => ({
        key: `p-${p.id}`,
        icon: <UserRound size={15} />,
        label: p.displayName,
        hint: p.jobTitle ?? undefined,
        run: () => navigate(`/people/${p.id}`),
      })),
    });
  if (r?.companies.length)
    rows.push({
      group: t('nav.companies'),
      rows: r.companies.map((c) => ({
        key: `a-${c.id}`,
        icon: <Building2 size={15} />,
        label: ar && c.nameAr ? c.nameAr : c.name,
        run: () => navigate(`/companies/${c.id}`),
      })),
    });
  if (r?.cases.length)
    rows.push({
      group: t('nav.cases'),
      rows: r.cases.map((c) => ({
        key: `c-${c.id}`,
        icon: <Briefcase size={15} />,
        label: `${c.number} · ${c.subject}`,
        run: () => navigate(`/cases/${c.id}`),
      })),
    });
  const flat = rows.flatMap((g) => g.rows);
  const current = Math.min(active, Math.max(0, flat.length - 1));

  const execute = async (row: Row | undefined) => {
    if (!row) return;
    setError(null);
    try {
      const message = await row.run();
      if (typeof message === 'string') {
        setDone(message);
        setText('');
      } else onClose();
    } catch {
      setError(t('common.error'));
    }
  };

  return (
    <div className="cmd-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="cmd-panel"
        role="dialog"
        aria-modal="true"
        aria-label={t('assistant.title')}
        data-testid="assistant"
      >
        <div className="cmd-input">
          <Search size={16} aria-hidden="true" className="faint" />
          <input
            ref={input}
            role="combobox"
            aria-expanded={flat.length > 0}
            aria-controls={listId}
            aria-activedescendant={flat[current] ? `${listId}-${flat[current]!.key}` : undefined}
            aria-label={t('assistant.title')}
            dir="auto"
            value={text}
            placeholder={t('assistant.placeholder')}
            onChange={(e) => {
              setText(e.target.value);
              setActive(0);
              setDone(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
              else if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActive((current + 1) % Math.max(1, flat.length));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive((current - 1 + flat.length) % Math.max(1, flat.length));
              } else if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                e.preventDefault();
                void execute(flat[current]);
              }
            }}
          />
          <VoiceInput
            onText={(said) => {
              setText(said);
              setActive(0);
              setDone(null);
              input.current?.focus();
            }}
            onError={setError}
          />
        </div>
        {done && (
          <div className="cmd-done" role="status" data-testid="assistant-done">
            {done}
          </div>
        )}
        {error && (
          <div className="form-error" role="alert" style={{ margin: '0.5rem 1rem' }}>
            {error}
          </div>
        )}
        <ul className="cmd-list" id={listId} role="listbox" aria-label={t('assistant.suggestions')}>
          {rows.map((g) => (
            <li key={g.group} role="presentation">
              <div className="cmd-group" role="presentation">
                {g.group}
              </div>
              <ul role="presentation" className="list-plain">
                {g.rows.map((row) => {
                  const idx = flat.indexOf(row);
                  return (
                    <li
                      key={row.key}
                      id={`${listId}-${row.key}`}
                      role="option"
                      aria-selected={idx === current}
                      className="cmd-row"
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => void execute(row)}
                    >
                      <span className="cmd-icon" aria-hidden="true">
                        {row.icon}
                      </span>
                      <span className="cmd-label" dir="auto">
                        {row.label}
                        {row.hint && <span className="cmd-hint">{row.hint}</span>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
        {!text && !done && (
          <div className="cmd-help">
            <strong>{t('assistant.tryTitle')}</strong>
            <ul className="list-plain">
              {t('assistant.examples')
                .split(' | ')
                .map((ex) => (
                  <li key={ex}>
                    <button type="button" className="cmd-example" onClick={() => setText(ex)} dir="auto">
                      {ex}
                    </button>
                  </li>
                ))}
            </ul>
            <p className="faint">{t('assistant.voiceNote')}</p>
          </div>
        )}
        {text && !flat.length && searchQ.length >= 2 && !results.isFetching && (
          <p className="faint cmd-empty">{t('assistant.nothing')}</p>
        )}
      </div>
    </div>
  );
}

/** The browser's speech recognition (not in every browser; the mic only shows where it is). */
interface Recognition {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}
const recognitionClass = () =>
  typeof window === 'undefined'
    ? undefined
    : (((window as unknown as Record<string, unknown>).SpeechRecognition ??
        (window as unknown as Record<string, unknown>).webkitSpeechRecognition) as
        (new () => Recognition) | undefined);

/**
 * Speak a command instead of typing it, in Arabic or English (the interface language): the browser turns it into
 * text and puts it in the box to check before anything runs.
 */
function VoiceInput({
  onText,
  onError,
}: {
  onText: (text: string) => void;
  onError: (e: string | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const [rec, setRec] = useState<Recognition | null>(null);
  const Rec = recognitionClass();
  if (!Rec) return null;
  const start = () => {
    onError(null);
    const r = new Rec();
    r.lang = i18n.language === 'ar' ? 'ar-QA' : 'en-US';
    r.interimResults = false;
    r.maxAlternatives = 1;
    r.onresult = (e) => {
      const said = Array.from(e.results)
        .map((x) => x[0]?.transcript ?? '')
        .join(' ')
        .trim();
      if (said) onText(said);
    };
    r.onerror = (e) =>
      onError(e.error === 'not-allowed' ? t('assistant.voice.noMic') : t('assistant.voice.failed'));
    r.onend = () => setRec(null);
    try {
      r.start();
      setRec(r);
    } catch {
      onError(t('assistant.voice.failed'));
    }
  };
  return (
    <button
      type="button"
      className={`icon-btn cmd-mic${rec ? ' is-recording' : ''}`}
      onClick={() => (rec ? rec.stop() : start())}
      aria-label={rec ? t('assistant.voice.stop') : t('assistant.voice.start')}
      title={rec ? t('assistant.voice.stop') : t('assistant.voice.start')}
      data-testid="voice-input"
    >
      {rec ? <Square size={14} /> : <Mic size={15} />}
    </button>
  );
}
